import React from 'react';
import { useApolloClient, ApolloClient, useQuery } from '@apollo/react-hooks';

import {
    SaveMerchantProductOfferMutationResponse,
    SaveMerchantProductOfferMutationVariables,
    unsaveMerchantProductOfferMutation,
    UnsaveMerchantProductOfferMutationResponse,
    UnsaveMerchantProductOfferMutationVariables,
} from '../../api/graphql/mutations/saveMerchantProductOffers';
import { saveMerchantProductOfferMutation } from '../../api/graphql/mutations/saveMerchantProductOffers';
import {
    UserSavedMerchantProductOffer,
    userSavedMerchantProductOffersQuery,
    UserSavedMerchantProductOffersQueryResponse,
} from '../../api/graphql/queries/savedMerchantProductOffers';
import { MerchantProductOffer } from './conversationMessages';
import { getUserId } from '../common/cognito';
import { logUserEventUtil } from '../events/userEvents';
import { mapSavedMerchantProductOfferToMerchantProductOffer } from './displayLogic';
import { TabType } from '../../pages/products/ProductDiscoveryPage';

/**
 * When a user unsaves a merchant product offer from the favorites tab, we don't want to set the state `savedMerchantProductOffers` immediately because we want these offers to still be displayed.
 * We do this to let the user save the offer again if they want to.
 * We use this hook to store the unsaved products still displayed in the state `unsavedMerchantProductOffersStillDisplayed`.The unsaved products will be removed from the state `savedMerchantProductOffers` when the user navigates back to the exploration tab or reloads the page.
 */
export function useUnsavedMerchantProductOffersStillDisplayed(
    displayedTab: TabType,
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>
): {
    unsavedMerchantProductOffersStillDisplayed: MerchantProductOffer[];
    setUnsavedMerchantProductOffersStillDisplayed: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
} {
    const [unsavedMerchantProductOffersStillDisplayed, setUnsavedMerchantProductOffersStillDisplayed] = React.useState<
        MerchantProductOffer[]
    >([]);
    const removeUnsavedStillDisplayedProducts = () => {
        const merchantProductOfferIdsToRemove = unsavedMerchantProductOffersStillDisplayed.map(
            (merchantProductOffer) => merchantProductOffer.merchantProductOfferId
        );
        setSavedMerchantProductOffers((previousSavedMerchantProductOffers: MerchantProductOffer[]) =>
            (previousSavedMerchantProductOffers ?? []).filter(
                (savedMerchantProductOffer) =>
                    !merchantProductOfferIdsToRemove.includes(savedMerchantProductOffer.merchantProductOfferId)
            )
        );
        setUnsavedMerchantProductOffersStillDisplayed([]);
    };
    React.useEffect(() => {
        if (displayedTab === TabType.exploration && unsavedMerchantProductOffersStillDisplayed.length > 0) {
            removeUnsavedStillDisplayedProducts();
        }
    }, [displayedTab]);
    return {
        unsavedMerchantProductOffersStillDisplayed,
        setUnsavedMerchantProductOffersStillDisplayed,
    };
}

export async function handleSaveMerchantProductOffer({
    offer,
    setSavedMerchantProductOffers,
    setUnsavedMerchantProductOffersStillDisplayed,
    saveMerchantProductOffer,
    isFavoritesTab,
    apolloClient,
    isSandboxEnvironment,
}: {
    offer: MerchantProductOffer;
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    setUnsavedMerchantProductOffersStillDisplayed?: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    saveMerchantProductOffer: (userSavedMerchantProductOffer: UserSavedMerchantProductOffer) => Promise<void>;
    isFavoritesTab: boolean;
    apolloClient: ApolloClient<object>;
    isSandboxEnvironment: boolean;
}) {
    const userId = getUserId();
    if (!offer.merchantProductOfferId || !offer.processedWebpageUrl || !userId) return;
    const newSavedOffer: UserSavedMerchantProductOffer = {
        merchantProductOfferId: offer.merchantProductOfferId,
        originalTitle: offer.originalTitle,
        processedWebpageUrl: offer.processedWebpageUrl,
        mainImageUrls: {
            defaultSize: offer.mainImageUrls.defaultSize,
        },
        merchantId: offer.merchantId ?? (offer.contextualInformation.originalMerchantName as string),
        userId,
        savedAt: Date.now(),
        displayPriceAmount: {
            currency: offer.priceInformation.displayPriceAmount.currency,
            valueInCents: offer.priceInformation.displayPriceAmount.valueInCents,
        },
    };
    // If we are in the favorites tab, we remove the merchant product offer from the `unsavedMerchantProductOffersStillDisplayed` state.
    if (isFavoritesTab && setUnsavedMerchantProductOffersStillDisplayed) {
        setUnsavedMerchantProductOffersStillDisplayed(
            (previousUnsavedMerchantProductOffers: MerchantProductOffer[]) =>
                previousUnsavedMerchantProductOffers?.filter(
                    (pendingUnsaveOffer) => pendingUnsaveOffer.merchantProductOfferId !== offer.merchantProductOfferId
                ) ?? []
        );
    } else {
        // If we're not in the favorites tab, we add the merchant product offer to the saved merchant product offers.
        setSavedMerchantProductOffers((previousSavedMerchantProductOffers: MerchantProductOffer[]) => [
            offer,
            ...previousSavedMerchantProductOffers,
        ]);
    }
    // We then save it to the database.
    try {
        await saveMerchantProductOffer(newSavedOffer);
        logUserEventUtil(apolloClient, {
            type: 'savedMerchantProductOffer',
            payload: {
                merchantProductOfferId: offer.merchantProductOfferId,
                productTitle: offer.originalTitle,
                merchantId: offer.merchantId ?? (offer.contextualInformation.originalMerchantName as string),
            },
        });
    } catch (error) {
        if (isSandboxEnvironment) console.error('Failed to save merchant product offer:', error);
    }
}

export function useSaveMerchantProductOffer() {
    const apolloClient = useApolloClient();
    const saveMerchantProductOffer = async (newSavedMerchantProductOffer: UserSavedMerchantProductOffer) => {
        await apolloClient.mutate<SaveMerchantProductOfferMutationResponse, SaveMerchantProductOfferMutationVariables>({
            mutation: saveMerchantProductOfferMutation,
            variables: {
                userSavedMerchantProductOffer: {
                    merchantProductOfferId: newSavedMerchantProductOffer.merchantProductOfferId,
                    originalTitle: newSavedMerchantProductOffer.originalTitle,
                    processedWebpageUrl: newSavedMerchantProductOffer.processedWebpageUrl,
                    mainImageUrls: newSavedMerchantProductOffer.mainImageUrls,
                    merchantId: newSavedMerchantProductOffer.merchantId,
                },
            },
        });
    };
    return saveMerchantProductOffer;
}

export async function handleUnsaveMerchantProductOffer({
    offer,
    setSavedMerchantProductOffers,
    setUnsavedMerchantProductOffersStillDisplayed,
    unsaveMerchantProductOffer,
    isFavoritesTab,
    isSandboxEnvironment,
    apolloClient,
}: {
    offer: MerchantProductOffer;
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    setUnsavedMerchantProductOffersStillDisplayed?: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    unsaveMerchantProductOffer: (userSavedMerchantProductOffer: UserSavedMerchantProductOffer) => Promise<void>;
    isFavoritesTab: boolean;
    isSandboxEnvironment: boolean;
    apolloClient: ApolloClient<object>;
}) {
    const userId = getUserId();
    if (!offer.merchantProductOfferId || !offer.processedWebpageUrl || !userId) return;
    // If we're in the favorites tab, we add the merchant product offer to the `unsavedMerchantProductOffersStillDisplayed` state.
    if (isFavoritesTab && setUnsavedMerchantProductOffersStillDisplayed)
        setUnsavedMerchantProductOffersStillDisplayed((previousUnsavedMerchantProductOffers: MerchantProductOffer[]) => [
            ...previousUnsavedMerchantProductOffers,
            offer,
        ]);
    else {
        // If we're not in the favorites tab, we update the state `savedMerchantProductOffers` immediately.
        setSavedMerchantProductOffers((previousSavedMerchantProductOffers: MerchantProductOffer[]) => [
            ...previousSavedMerchantProductOffers.filter(
                (savedMerchantProductOffer) =>
                    savedMerchantProductOffer.merchantProductOfferId !== offer.merchantProductOfferId
            ),
        ]);
    }
    try {
        await unsaveMerchantProductOffer({
            merchantProductOfferId: offer.merchantProductOfferId,
            originalTitle: offer.originalTitle,
            processedWebpageUrl: offer.processedWebpageUrl,
            mainImageUrls: offer.mainImageUrls,
            merchantId: offer.merchantId ?? (offer.contextualInformation.originalMerchantName as string),
            userId,
            savedAt: Date.now(),
            displayPriceAmount: offer.priceInformation.displayPriceAmount,
        });
        logUserEventUtil(apolloClient, {
            type: 'unsavedMerchantProductOffer',
            payload: {
                merchantProductOfferId: offer.merchantProductOfferId,
                productTitle: offer.originalTitle,
                merchantId: offer.merchantId ?? (offer.contextualInformation.originalMerchantName as string),
            },
        });
    } catch (error) {
        if (isSandboxEnvironment) console.error('Failed to unsave merchant product offer:', error);
    }
}

export function useUnsaveMerchantProductOffer() {
    const apolloClient = useApolloClient();
    const unsaveMerchantProductOffer = async (userSavedMerchantProductOffer: UserSavedMerchantProductOffer) => {
        await apolloClient.mutate<UnsaveMerchantProductOfferMutationResponse, UnsaveMerchantProductOfferMutationVariables>({
            mutation: unsaveMerchantProductOfferMutation,
            variables: {
                merchantProductOfferId: userSavedMerchantProductOffer.merchantProductOfferId,
            },
        });
    };
    return unsaveMerchantProductOffer;
}

export function useUserSavedMerchantProductOffers() {
    const { data, loading } = useQuery<UserSavedMerchantProductOffersQueryResponse>(userSavedMerchantProductOffersQuery, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'standby',
    });
    const [savedMerchantProductOffers, setSavedMerchantProductOffers] = React.useState<MerchantProductOffer[]>([]);
    React.useEffect(() => {
        if (!loading && data?.user?.savedMerchantProductOffers)
            setSavedMerchantProductOffers(
                data.user.savedMerchantProductOffers.map(mapSavedMerchantProductOfferToMerchantProductOffer)
            );
    }, [data, loading]);
    return [savedMerchantProductOffers, setSavedMerchantProductOffers] as const;
}
